<template>
  <div
    ref="pillDropdown"
    class="pill-dropdown"
    :style="[primaryButtonColors, secondaryButtonColors]"
  >
    <div class="dropdown-options">
      <button
        ref="dropdown-trigger"
        class="dropdown-trigger"
        :aria-expanded="isDropdownOpen"
        aria-haspopup="listbox"
        :aria-label="$t('search.filterDropdownAriaLabel', { filterName: name, selectedCount: appliedOptions.length })"
        :aria-labelledby="isDropdownOpen ? 'options-label' : undefined"
        :aria-controls="isDropdownOpen ? 'options-list' : undefined"
        :class="appliedOptions.length > 0 ? 'has-selected-vals' : ''"
        @click="toggleDropdown"
        @keydown="handleTriggerKeydown"
        @focus="emit('closeSuggestionsDropdown')"
      >
        <span class="name">{{ dynamicButtonName }}</span>
        <Icon
          name="lucide:chevron-down"
          size="14"
          aria-hidden="true"
        />
      </button>

      <div
        v-if="isDropdownOpen"
        class="options-list-container"
        :class="{ 'thin-dropdown-width': isThinDropdownWidth }"
        :style="dropdownHeight"
      >
        <div
          v-if="showSearch"
          class="search-section"
        >
          <div class="search-box">
            <Icon
              name="lucide:search"
              size="16"
              aria-hidden="true"
              class="search-icon"
            />
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              class="search-input"
              :placeholder="`${$t('search.searchPlaceholder', { name: name.toLowerCase() })}...`"
              @click.stop
              @keydown.escape.exact="closeDropdown"
              @keydown.shift.tab.exact="closeDropdown"
            />
          </div>
        </div>
        <label
          id="options-label"
          class="sr-only"
        >
          {{ name }} {{ $t('search.options') }}
        </label>
        <div
          ref="optionsList"
          role="listbox"
          class="options-list"
          aria-labelledby="options-label"
          :aria-multiselectable="props.allowMultiSelect"
          :aria-activedescendant="accessibility.activeDescendantId.value"
          tabindex="0"
          @keydown="handleListboxKeydown"
          @focus="handleListboxFocus"
          @blur="handleBlur"
        >
          <ul
            class="options-list-ul"
            role="presentation"
          >
            <li
              v-if="showSelectAll"
              id="option-0"
              ref="option-0"
              key="select-all"
              role="option"
              :aria-selected="selectAllSelectionState?.checked"
              :class="{
                'show-checkbox': allowMultiSelect,
                focused: accessibility.focusedIndex.value === 0,
                'select-all-option': true,
                indeterminate: selectAllSelectionState?.indeterminate,
              }"
              @click.prevent.stop="toggleSelectAll"
              @mouseenter="handleOptionMouseEnter(0)"
            >
              {{ $t('search.selectAll') }}
            </li>
            <li
              v-for="(option, index) in pillOptions"
              :id="`option-${showSelectAll ? index + 1 : index}`"
              :ref="`option-${showSelectAll ? index + 1 : index}`"
              :key="index"
              role="option"
              :aria-selected="pillOptions[index].checked"
              :class="{
                'show-checkbox': allowMultiSelect,
                focused: accessibility.focusedIndex.value === (showSelectAll ? index + 1 : index),
              }"
              @click.prevent.stop="checkOption(index)"
              @mouseenter="handleOptionMouseEnter(showSelectAll ? index + 1 : index)"
            >
              {{ option.name }}
            </li>
          </ul>
        </div>
        <div
          v-if="allowMultiSelect"
          class="clear-section"
        >
          <button
            class="clear-button"
            :disabled="selectedOptions.length === 0"
            :aria-label="$t('search.clearFiltersAriaLabel', { filterName: name })"
            @click.prevent.stop="clearFilters"
            @keydown.enter.prevent.stop="clearFilters"
            @keydown.space.prevent.stop="clearFilters"
            @blur="handleBlur"
          >
            {{ $t('search.clear') }}
          </button>
          <button
            class="apply-button"
            :disabled="!hasUnappliedChanges || selectedOptions.length === 0"
            :aria-label="$t('search.applyFiltersAriaLabel', { filterName: name })"
            @click="applyFilters"
            @keydown.enter.prevent.stop="applyFilters"
            @keydown.space.prevent.stop="applyFilters"
            @blur="handleBlur"
          >
            {{ $t('search.apply') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onClickOutside, useArrayDifference } from '@vueuse/core';
import { useThemedButtons } from '@/composables/useThemedButtons';
import { useAccessibleDropdown } from '@/composables/useAccessibleDropdown';

const { primaryButtonColors, secondaryButtonColors } = useThemedButtons();

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  name: {
    type: String,
    default: '',
  },
  allowMultiSelect: {
    type: Boolean,
    default: true,
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
  dynamicHeight: {
    type: Boolean,
    default: true,
  },
  // date dropdown is smaller than the other dropdowns
  isThinDropdownWidth: {
    type: Boolean,
    default: false,
  },
  showSelectAll: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['closeSuggestionsDropdown', 'clearFilters', 'applyFilters', 'filterApplied']);

const isDropdownOpen = ref(false);
const searchQuery = ref('');

const pillDropdown = useTemplateRef('pillDropdown');
const searchInput = useTemplateRef('searchInput');
const optionsList = useTemplateRef('optionsList');
const dropdownTrigger = useTemplateRef('dropdown-trigger');

const { $t } = useNuxtApp();

// Use the options prop directly - it's already reactive
const allOptions = computed(() => props.options);
const selectedOptions = computed(() => allOptions.value.filter((option) => option.checked === true));
const appliedOptions = ref([]);

// Compute the selection state for "Select All"
const selectAllSelectionState = computed(() => {
  if (!props.showSelectAll) {
    return null;
  }

  // Calculate visible options based on search query
  let visibleOptions = allOptions.value;
  if (props.showSearch && searchQuery.value.trim()) {
    visibleOptions = allOptions.value.filter((option) =>
      option.name.toLowerCase().includes(searchQuery.value.toLowerCase()),
    );
  }

  const selectedCount = visibleOptions.filter((option) => option.checked).length;
  const totalCount = visibleOptions.length;

  if (selectedCount === 0) {
    return { checked: false, indeterminate: false };
  } else if (selectedCount === totalCount) {
    return { checked: true, indeterminate: false };
  } else {
    return { checked: false, indeterminate: true };
  }
});

// Watch for external filter clearing by detecting when ALL options are unchecked simultaneously ("Clear all" button)
// Also initialize appliedOptions with pre-checked options
watch(
  () => props.options,
  (newOptions, oldOptions) => {
    // If no options are checked, clear appliedOptions
    if (newOptions.every((option) => !option.checked)) {
      appliedOptions.value = [];
    } else if (!oldOptions && appliedOptions.value.length === 0 && newOptions.some((option) => option.checked)) {
      // Initialize appliedOptions with pre-checked options when component first loads
      appliedOptions.value = newOptions.filter((option) => option.checked);
    }
  },
  { deep: true, immediate: true },
);

// Filter options based on search query
const pillOptions = computed(() => {
  if (!props.showSearch || !searchQuery.value.trim()) {
    return allOptions.value;
  }

  return allOptions.value.filter((option) => option.name.toLowerCase().includes(searchQuery.value.toLowerCase()));
});

const dropdownMaxHeight = computed(() => {
  const DROPDOWN_WITH_SEARCH_MAX_HEIGHT = 410;
  const DROPDOWN_WITHOUT_SEARCH_MAX_HEIGHT = 362;
  return props.showSearch ? DROPDOWN_WITH_SEARCH_MAX_HEIGHT : DROPDOWN_WITHOUT_SEARCH_MAX_HEIGHT;
});
// Calculate dropdown height based on fixed max heights
const dropdownHeight = computed(() => {
  return { '--dropdown-height': `${dropdownMaxHeight.value}px` };
});

const hasUnappliedChanges = computed(() => {
  const difference = useArrayDifference(
    selectedOptions.value,
    appliedOptions.value,
    (value, othVal) => value.name === othVal.name,
    { symmetric: true },
  );
  return difference.value.length > 0 || selectedOptions.value.length === 0;
});

const dynamicButtonName = computed(() => {
  if (appliedOptions.value.length >= 2) {
    return '2+ selected';
  } else if (appliedOptions.value.length === 1) {
    return appliedOptions.value[0].name;
  } else {
    return props.name;
  }
});

const accessibility = useAccessibleDropdown({
  onClose: closeDropdown,
  scrollBehavior: 'smooth',
  typeAheadTimeout: 500,
  wrapNavigation: true,
});

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
  if (isDropdownOpen.value) {
    nextTick(() => {
      forceFocusOnListbox();
    });
  }
}

function closeDropdown() {
  isDropdownOpen.value = false;
  searchQuery.value = ''; // Clear search when closing
  accessibility.clearFocusedIndex();
  nextTick(() => {
    if (dropdownTrigger.value) {
      dropdownTrigger.value.focus();
    }
  });
}

function forceFocusOnListbox() {
  if (props.showSearch && searchInput.value) {
    searchInput.value.focus();
  } else {
    if (optionsList.value) {
      optionsList.value.focus();
    }
  }
}

function handleTriggerKeydown(event) {
  accessibility.handleTriggerKeydown(event, isDropdownOpen.value, toggleDropdown);
}

function handleListboxKeydown(event) {
  // Create a combined list with Select All if needed
  const allOptionsForKeyboard = props.showSelectAll
    ? [
        {
          name: $t('search.selectAll'),
          checked: selectAllSelectionState.value?.checked,
          isSelectAll: true,
        },
        ...pillOptions.value,
      ]
    : pillOptions.value;

  accessibility.handleListboxKeydown(event, allOptionsForKeyboard, 'option', {
    onSelect: (index) => {
      if (props.showSelectAll && index === 0) {
        toggleSelectAll();
      } else {
        checkOption(props.showSelectAll ? index - 1 : index);
      }
    },
    onToggleSelection: (index) => {
      if (props.showSelectAll && index === 0) {
        toggleSelectAll();
      } else {
        checkOption(props.showSelectAll ? index - 1 : index);
      }
    },
    returnFocusSelector: '.dropdown-trigger',
    optionNameGetter: (option) => option.name,
    allowMultiSelect: props.allowMultiSelect,
  });
}

function handleListboxFocus() {
  const allOptionsForKeyboard = props.showSelectAll
    ? [
        {
          name: $t('search.selectAll'),
          checked: selectAllSelectionState.value?.checked,
          isSelectAll: true,
        },
        ...pillOptions.value,
      ]
    : pillOptions.value;

  accessibility.handleListboxFocus(allOptionsForKeyboard, 'option', (option) => option.checked);
}

onUnmounted(() => {
  accessibility.cleanup();
});

onClickOutside(pillDropdown, () => {
  if (isDropdownOpen.value) {
    closeDropdown();
  }
});

function clearFiltersFromRoute() {
  emit('clearFilters');
}

function toggleSelectAll() {
  const targetOptions = pillOptions.value;
  const allSelected = targetOptions.every((option) => option.checked);
  const newState = !allSelected;
  targetOptions.forEach((option) => {
    option.checked = newState;
  });

  // If deselecting all, auto-fire the clear request (same behavior as unchecking last filter)
  if (!newState) {
    clearFiltersFromRoute();
  }
}

function checkOption(index) {
  // Find the actual option in the original array
  const filteredOption = pillOptions.value[index];
  const originalIndex = allOptions.value.findIndex((option) => {
    // Match by name and org_id (if it exists) or other unique identifiers
    if (option.org_id && filteredOption.org_id) {
      return option.name === filteredOption.name && option.org_id === filteredOption.org_id;
    } else if (option.type && filteredOption.type) {
      return option.name === filteredOption.name && option.type === filteredOption.type;
    } else {
      return option.name === filteredOption.name;
    }
  });
  if (originalIndex !== -1 && props.allowMultiSelect) {
    allOptions.value[originalIndex].checked = !allOptions.value[originalIndex].checked;
  }

  if (!props.allowMultiSelect) {
    const originalVal = allOptions.value[originalIndex].checked;
    allOptions.value.forEach((option) => {
      option.checked = false;
    });
    allOptions.value[originalIndex].checked = !originalVal;
    selectedOptions.value.length === 0 ? clearFiltersFromRoute() : applyFilters();
  }
  // When no filters are selected, no filters should be applied. SO-957
  if (allOptions.value.every((option) => !option.checked)) {
    clearFiltersFromRoute();
  }
}

function clearFilters() {
  appliedOptions.value = [];
  allOptions.value.forEach((option) => {
    option.checked = false;
  });

  clearFiltersFromRoute();

  nextTick(() => {
    if (optionsList.value) {
      optionsList.value.focus();
      const totalOptions = props.showSelectAll ? pillOptions.value.length + 1 : pillOptions.value.length;
      accessibility.setFocusedIndex(0, totalOptions, 'option');
    }
  });
}

function applyFilters() {
  appliedOptions.value = selectedOptions.value;
  emit('applyFilters');

  if (appliedOptions.value.length > 0) {
    emit('filterApplied', {
      filter_type: props.name.toLowerCase(),
      filter_value: appliedOptions.value.map((option) => option.name).toString(),
    });
  }

  closeDropdown();
}

//fixes a bug if list box element is focused and user hover over another element. Should be only one highlighted element
function handleOptionMouseEnter(index) {
  accessibility.setFocusedIndex(index, pillOptions.value.length, 'option');
}

function handleBlur(event) {
  if (!event.relatedTarget) {
    return;
  }

  accessibility.handleListboxBlur(event, pillDropdown, closeDropdown, [
    '.clear-button',
    '.apply-button',
    '.options-list',
  ]);
}
</script>

<style scoped lang="scss">
.pill-dropdown {
  --space-100: 4px;
  --space-150: 6px;
  --space-200: 8px;
  --space-300: 12px;
  --space-400: 16px;
  --color-blue-500: #1850c1;
  --color-white: #fff;
  --color-grey-50: #f7f7f7;
  --color-grey-75: #e9e9e9;
  --color-grey-100: #e0e0e0;
  --color-grey-200: #d3d3d3;
  --color-grey-950: #2d2d2d;
  --color-text-tertiary: #949494;
  --border-radius-xs: 4px;
}

.dropdown-trigger {
  display: flex;
  border-radius: var(--secondary-button-border-radius);
  border: var(--secondary-button-border);
  background: var(--secondary-button-background);
  justify-content: center;
  align-items: center;
  padding: var(--space-150) var(--space-300);
  gap: var(--space-150);
  cursor: pointer;
  color: var(--secondary-button-text);

  &:focus-visible {
    outline: var(--secondary-button-focus-outline);
    outline-offset: 1px;
    border: var(--secondary-button-hover-border);
  }

  &:hover:not(:disabled) {
    background-color: var(--secondary-button-hover);
    border: var(--secondary-button-hover-border);
  }
  &.has-selected-vals {
    border-radius: var(--secondary-button-border-radius);
    border: var(--secondary-button-border);
    background: var(--secondary-button-active);
    color: var(--secondary-button-text);
  }
}

.dropdown-options {
  display: flex;
  align-items: flex-start;
  gap: var(--space-200);
  align-self: stretch;
  background: var(--color-white);
  position: relative;
}

.name {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.1px;
}

.options-list-container {
  position: absolute;
  top: 34px;
  border-radius: var(--space-100);
  border: 1px solid var(--color-grey-100);
  background: var(--color-white);
  min-width: clamp(280px, 90vw, 340px);
  max-height: var(--dropdown-height);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.thin-dropdown-width {
    min-width: 207px;
  }

  .search-section {
    padding: var(--space-400) var(--space-400) var(--space-100) var(--space-400);
    background: var(--color-white);
    width: -webkit-fill-available;
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .search-box {
    display: flex;
    align-items: center;
    gap: var(--space-200);
    padding: var(--space-300);
    border-radius: var(--border-radius-xs);
    border: 1px solid var(--color-grey-75);
    background: var(--color-grey-50);
    box-sizing: border-box;
    transition: border-color 0.2s ease;

    &:focus-within {
      outline: 2px solid var(--color-blue-500);
      outline-offset: -2px;
      border-color: var(--color-blue-500);
    }
  }

  .search-icon {
    color: var(--color-grey-950);
    flex-shrink: 0;
  }

  .search-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 12px;
    letter-spacing: 0.1px;
    font-weight: 400;
    line-height: 16px;
    color: var(--color-grey-950);
    background: var(--color-grey-50);

    &::placeholder {
      color: var(--color-text-tertiary);
    }
  }
}

.options-list {
  display: flex;
  align-items: flex-start;
  gap: var(--space-100);
  align-self: stretch;
  box-sizing: border-box;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &:focus-visible {
    outline: 2px solid var(--color-blue-500);
    outline-offset: -2px;
  }

  .sr-only {
    clip: rect(1px, 1px, 1px, 1px);
    clip-path: inset(50%);
    height: 1px;
    width: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
  }
}

.clear-section {
  padding: var(--space-300) var(--space-400);
  display: flex;
  justify-content: space-between;
  box-sizing: border-box;
  border-top: 1px solid var(--color-grey-75);
  background: var(--color-white);
  width: 100%;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  z-index: 2;

  button {
    padding: var(--space-200) var(--space-400);
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-radius: var(--space-100);

    &:focus-visible {
      outline: 2px solid var(--color-blue-500);
      outline-offset: 2px;
    }

    &:hover:not(:disabled) {
      opacity: 0.8;
    }

    &:disabled {
      background: var(--disabled-button-background);
      color: var(--disabled-button-text-contrast);
      border: var(--disabled-button-border);
      cursor: default;
    }
  }

  .clear-button {
    background: var(--color-white);
    color: var(--tertiary-button-text-contrast);

    &:disabled {
      background: transparent;
      color: var(--disabled-button-tertiary-text-contrast);
    }
  }

  .apply-button {
    border-radius: var(--secondary-button-border-radius);
    background: var(--primary-button-background);
    color: var(--color-white);
  }
}

.options-list-ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
}

li {
  display: flex;
  align-items: center;
  list-style: none;
  padding: var(--space-400);
  color: var(--color-grey-950);
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.1px;
  cursor: pointer;
  position: relative;

  &:hover {
    background: var(--secondary-button-hover); /* fallback */
    background: color-mix(in srgb, var(--primary-button-background) 10%, #fff);
  }

  &.focused {
    background: var(--secondary-button-hover); /* fallback */
    background: color-mix(in srgb, var(--primary-button-background) 10%, #fff);
  }
}

li.show-checkbox[role='option']::before {
  position: relative;
  top: -1px;
  left: calc(-1 * var(--space-200));
  right: var(--space-100);
  height: 16px;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='16' width='16' style='forced-color-adjust: auto;'%3E%3Crect x='2' y='2' height='13' width='13' rx='2' stroke='currentcolor' stroke-width='1' fill-opacity='0' /%3E%3C/svg%3E");
}

li.show-checkbox[role='option'][aria-selected='true']::before {
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='16' width='16' style='forced-color-adjust: auto;'%3E%3Crect x='2' y='2' height='13' width='13' rx='2' stroke='currentcolor' stroke-width='1' fill-opacity='0' /%3E%3Cpolyline points='4,8 7,12 12,5' fill='none' stroke='currentcolor' stroke-width='2' /%3E%3C/svg%3E");
}

li.show-checkbox[role='option'].indeterminate::before {
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='16' width='16' style='forced-color-adjust: auto;'%3E%3Crect x='2' y='2' height='13' width='13' rx='2' stroke='currentcolor' stroke-width='1' fill-opacity='0' /%3E%3Cline x1='5' y1='8.5' x2='12' y2='8.5' stroke='currentcolor' stroke-width='2' /%3E%3C/svg%3E");
}

li.select-all-option {
  border-bottom: 1px solid var(--color-grey-75);
}

@media (max-width: 600px) {
  .dropdown-options {
    position: static;
  }

  .options-list-container {
    top: 52px;
    left: 16px;
    right: 16px;
  }
}

@media (prefers-contrast: high) {
  .dropdown-trigger:focus,
  .options-list:focus {
    outline: 3px solid;
  }

  li.focused {
    border: 3px solid;
  }
}

@media (prefers-reduced-motion: reduce) {
  .options-list,
  li {
    transition: none;
  }
}
</style>
